import { Set } from 'immutable'
import { Updater, AsyncState, loadingAsyncState, unloadedAsyncState } from 'widgets-for-react'
import { GetResearchByPatients } from './researchOverviewApi'
import { processResearch } from './researchOverviewUtils'
import { AZNavigationState, initialAZNavigationState } from '../azNavigation/azNavigationState'
import { Alphabetized } from '../azNavigation/azNavigationUtils'

export type ResearchOverviewState = {
  patientsNavigationState: AZNavigationState
  noPatientsNavigationState: AZNavigationState
  researchPerPatientsState: AsyncState<ResearchPerPatientsNeeded>
  patientsProcessedResearchState: AsyncState<ProcessedResearch>
  noPatientsProcessedResearchState: AsyncState<ProcessedResearch>
}

export type ResearchPerPatientsNeeded = {
  patientsNeeded: ResearchWithDepartments[]
  noPatientsNeeded: ResearchWithDepartments[]
}

export type ResearchWithDepartments = {
  departments: string[]
} & Research

export type Research = {
  title: string
  intro: string
  topic: string
  url: string
}

export type ProcessedResearch = {
  departments: Set<string>
  groups: Alphabetized<ResearchPerDepartment>
}

export type ResearchPerDepartment = {
  department: string
  research: Research[]
}

export const initialResearchOverviewState = (
  filter?: string,
  filter2?: string,
  query?: string,
  query2?: string
): ResearchOverviewState => ({
  patientsNavigationState: initialAZNavigationState(filter, undefined, query, undefined),
  noPatientsNavigationState: initialAZNavigationState(undefined, filter2, undefined, query2),
  researchPerPatientsState: loadingAsyncState(() => GetResearchByPatients()),
  patientsProcessedResearchState: unloadedAsyncState(),
  noPatientsProcessedResearchState: unloadedAsyncState(),
})

export const researchOverviewStateUpdaters = {
  updatePatientsNavigationState:
    (updater: Updater<AZNavigationState>) =>
    (currentState: ResearchOverviewState): ResearchOverviewState => {
      const newState = updater(currentState.patientsNavigationState)
      const researchState = currentState.researchPerPatientsState
      if (newState.submit && researchState.kind == 'loaded') {
        return {
          ...currentState,
          patientsNavigationState: { ...newState, submit: false },
          patientsProcessedResearchState: loadingAsyncState(() =>
            processResearch(researchState.value.patientsNeeded, newState.filter, newState.query)
          ),
        }
      } else
        return {
          ...currentState,
          patientsNavigationState: { ...newState, submit: false },
        }
    },
  updateNoPatientsNavigationState:
    (updater: Updater<AZNavigationState>) =>
    (currentState: ResearchOverviewState): ResearchOverviewState => {
      const newState = updater(currentState.noPatientsNavigationState)
      const researchState = currentState.researchPerPatientsState
      if (newState.submit && researchState.kind == 'loaded') {
        return {
          ...currentState,
          noPatientsNavigationState: { ...newState, submit: false },
          noPatientsProcessedResearchState: loadingAsyncState(() =>
            processResearch(researchState.value.noPatientsNeeded, newState.filter2, newState.query2)
          ),
        }
      } else
        return {
          ...currentState,
          noPatientsNavigationState: { ...newState, submit: false },
        }
    },
  updateResearchPerPatientsNeededState:
    (updater: Updater<AsyncState<ResearchPerPatientsNeeded>>) =>
    (currentState: ResearchOverviewState): ResearchOverviewState => {
      const s1 = updater(currentState.researchPerPatientsState)
      return {
        ...currentState,
        researchPerPatientsState: s1,
        patientsProcessedResearchState:
          currentState.patientsProcessedResearchState.kind != 'loaded' && s1.kind == 'loaded'
            ? loadingAsyncState(() =>
                processResearch(
                  s1.value.patientsNeeded,
                  currentState.patientsNavigationState.filter,
                  currentState.patientsNavigationState.query
                )
              )
            : currentState.patientsProcessedResearchState,

        noPatientsProcessedResearchState:
          currentState.noPatientsProcessedResearchState.kind != 'loaded' && s1.kind == 'loaded'
            ? loadingAsyncState(() =>
                processResearch(
                  s1.value.noPatientsNeeded,
                  currentState.noPatientsNavigationState.filter2,
                  currentState.noPatientsNavigationState.query2
                )
              )
            : currentState.noPatientsProcessedResearchState,
      }
    },
  updatePatientsProcessedResearchState:
    (updater: Updater<AsyncState<ProcessedResearch>>) =>
    (currentState: ResearchOverviewState): ResearchOverviewState => ({
      ...currentState,
      patientsProcessedResearchState: updater(currentState.patientsProcessedResearchState),
    }),
  updateNoPatientsProcessedResearchState:
    (updater: Updater<AsyncState<ProcessedResearch>>) =>
    (currentState: ResearchOverviewState): ResearchOverviewState => ({
      ...currentState,
      noPatientsProcessedResearchState: updater(currentState.noPatientsProcessedResearchState),
    }),
}
